import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EventsState } from 'modules/Events/Event.interfaces';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player';
import { Button } from 'antd';
import Lottie from 'react-lottie';
import { getUserLanguage, showInfo } from 'utils/helpers';
import { PlayIcon } from 'components/icons/PlayIcon';
import { PauseIcon } from 'components/icons/PauseIcon';
import { handleOpenPlayer } from 'modules/Events/Events.actions';
import { useWindowSize } from 'utils/hooks';
import {
  AudioWrapper,
  CardWrapper,
  DescriptionCard,
  FLexWrapper,
  ImgWrapper,
  MobileBookingRow,
  PriceArea,
  TitleArea,
  TranscriptionPlayer,
} from './Player.style';
import Avatar from 'assets/images/avatar.svg';
import ErrorImg from 'assets/images/error.png';
import 'react-h5-audio-player/lib/styles.css';
import { ContainerWrapper, IContainer } from 'assets/styles/Container.style';
import AudioTrack from 'assets/audio/Anto virgule 3.1 (1).mp3';
import { StyledComponent, useTheme } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { BackIcon } from 'components/icons/BackIcon';
import { ForwardIcon } from 'components/icons/ForwardIcon';
import { MdOutlineSubtitles, MdOutlineSubtitlesOff } from 'react-icons/md';
import { GlobalState } from '../../../modules/Global/Global.interfaces';
import { postAudioPlayed, putAudioStopped } from '../../../modules/Statistics/Statistics.actions';
import { StatisticsState } from '../../../modules/Statistics/Statistics.interfaces';
import { ALL_INCLUSIVE, MULTI_CHANNEL } from '../../../interfaces';
import envVariables from '../../../constant';

const audio = new Audio(AudioTrack);

interface ICaption {
  start: number;
  end: number;
  text: string;
}

export const Player = () => {
  const {
    playerList: { list, account, index },
  } = useSelector(({ eventsReducer }: { eventsReducer: EventsState }) => eventsReducer);
  const { audio_played_id } = useSelector(
    ({ statisticsReducer }: { statisticsReducer: StatisticsState }) => statisticsReducer,
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { width } = useWindowSize();
  const isMobile = width && width < 768;
  const ref = useRef<StyledComponent<'div', any, IContainer, never>>(null);
  const isEndingRef = useRef(false); // true while handling onEnded -> next
  const firstOpenRef = useRef(true);
  const [ind, setInd] = useState<number>(index || 0);
  const activeIndex = account && account.length > 0 ? Math.max(0, Math.min(ind || 0, account.length - 1)) : 0;
  const currentItem = account && account.length > 0 ? account[activeIndex] : undefined;
  const [avatarImg, setAvatarImg] = useState(currentItem?.avatar || Avatar);
  const [animating, setAnimating] = useState<boolean>(false);
  const [animationHeight, setAnimationHeight] = useState(0);
  const [animation, setAnimation] = useState();
  const [isHidden, setIsHidden] = useState(false);
  const [subtitles, setSubtitles] = useState<ICaption[]>([]);
  const [currentSubtitle, setCurrentSubtitle] = useState<string>('');
  const introDuration = 2.1; // Duration of the intro in seconds
  const [playerCurrentTime, setPlayerCurrentTime] = useState(0);
  const playerCurrentTimeRef = useRef(playerCurrentTime);
  const audioEventIdRef = useRef(audio_played_id);
  const eventIdRef = useRef(currentItem?.id || 0);
  const { iframeParams } = useSelector(({ globalReducer }: { globalReducer: GlobalState }) => globalReducer);
  const player = useRef<AudioPlayer>(null);
  const lng = useMemo(() => {
    return getUserLanguage().lng;
  }, []);

  // Track if this is the initial opening of the player
  const [isInitialOpen, setIsInitialOpen] = useState(true);

  // Track if audio-played has been sent for current track
  const audioPlayedSentRef = useRef(false);

  // Global pause/mute preference
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);

  // Persistent state for whether audio should play when changing tracks
  const [shouldPlay, setShouldPlay] = useState(true);
  const shouldPlayRef = useRef(true);
  const readMuted = () => {
    const s = sessionStorage.getItem('globalMuted');
    // Default to unmuted if no preference is set
    return s === null ? false : s === 'true';
  };
  const [isMuted, setIsMuted] = useState(readMuted());
  const isMutedRef = useRef(readMuted());

  // Check if current item is a video
  const isCurrentVideo = currentItem?.isVideo;

  // Sync mute state whenever it changes
  useEffect(() => {
    isMutedRef.current = isMuted;
    // The AudioPlayer will handle muting via the muted prop
    // Apply to current video if it exists (but not on mobile - videos always muted there)
    if (!isMobile) {
      const mediaMap = (window as any).__storyMedia || {};
      const videoRef: HTMLVideoElement | undefined = mediaMap?.[activeIndex]?.video;
      if (videoRef) {
        videoRef.muted = isMuted;
      }
    }
  }, [isMuted, activeIndex, isMobile]);

  useEffect(() => {
    audioEventIdRef.current = audio_played_id;
  }, [audio_played_id]);

  useEffect(() => {
    if (ref && ref.current) {
      setAnimationHeight(ref.current.clientHeight);
    }

    async function fetchAnimation() {
      const response = await fetch(envVariables.lottieAnimation);
      const anim = await response.json();
      setAnimation(anim);
    }

    fetchAnimation();
    return () => {
      if (audioEventIdRef.current) {
        dispatch(
          putAudioStopped.request({
            event_detail_id: eventIdRef.current,
            id: audioEventIdRef.current,
            duration: playerCurrentTimeRef.current,
          }),
        );
      }
    };
  }, []);
  const theme: any = useTheme();
  const playerColor = theme?.primary?.[5] || '#2C2C2C';
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid meet',
    },
    duration: 3000,
  };
  useEffect(() => {
    setIsHidden(false);

    // when the player receives a brand-new playlist, reset play state once
    if (list.length && (index || index === 0)) {
      setInd(index || 0);

      if (firstOpenRef.current) {
        setIsInitialOpen(true);
        setIsPaused(false);
        isPausedRef.current = false;
        setShouldPlay(true);
        shouldPlayRef.current = true;
        firstOpenRef.current = false; // prevent future resets when just moving between items
      }
    } else {
      setInd(0);
      firstOpenRef.current = true; // allow reset next time a non-empty list is opened
    }
  }, [list, index]);

  useEffect(() => {
    eventIdRef.current = currentItem?.id || 0;

    // When track changes, stop the previous audio tracking
    if ((ind || ind === 0) && audioEventIdRef.current) {
      dispatch(
        putAudioStopped.request({
          id: audioEventIdRef.current,
          duration: playerCurrentTimeRef.current,
        }),
      );
    }

    // Reset the audio-played flag when track changes
    audioPlayedSentRef.current = false;

    // Apply mute preference to current video as well (but not on mobile - videos always muted there)
    if (!isMobile) {
      const mediaMap = (window as any).__storyMedia || {};
      const videoRef: HTMLVideoElement | undefined = mediaMap?.[activeIndex]?.video;
      if (videoRef) {
        videoRef.muted = isMutedRef.current;
      }
    }
  }, [ind, currentItem, activeIndex]);

  useEffect(() => {
    setSubtitles([]);
    const transcription = currentItem?.transcription;
    if (transcription) {
      setSubtitles(parseSRT(transcription));
    }
    setAvatarImg(currentItem?.avatar || Avatar);
  }, [ind, list, currentItem]);

  useEffect(() => {
    playerCurrentTimeRef.current = playerCurrentTime;
    const currentTime = playerCurrentTime + introDuration;
    const subtitle = subtitles.find((sub) => currentTime >= sub.start && currentTime <= sub.end);
    setCurrentSubtitle(subtitle ? subtitle.text : '');
  }, [playerCurrentTime, subtitles]);

  const parseSRT = (srt: string): ICaption[] => {
    const normalizedSRT = srt.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const blocks = normalizedSRT.split('\n\n');

    return blocks
      .filter((block) => block.includes(' --> '))
      .map((block) => {
        const lines = block.split('\n');
        const index = lines[0];
        const time = lines[1];
        const text = lines.slice(2).join(' ');

        const [start, end] = time.split(' --> ');

        return {
          start: convertTimeToSeconds(start),
          end: convertTimeToSeconds(end),
          text: text,
        };
      });
  };

  const convertTimeToSeconds = (time: string) => {
    const [hours, minutes, seconds] = time.split(':');
    const [mainSeconds, ms] = seconds.split(',');
    return parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(mainSeconds) + parseInt(ms) / 1000;
  };

  const onControlClick = (next: boolean, isFromOnEnded = false) => () => {
    // On mobile, sync with story scrolling but maintain play state
    if (isMobile && (window as any).__mobileStoriesNavigation) {
      const nav = (window as any).__mobileStoriesNavigation;
      if (next) {
        nav.next();
      } else {
        nav.previous();
      }
      // Mobile navigation doesn't change our index directly,
      // but we still need to maintain the play state
      return;
    }

    // Desktop behavior
    audio.pause();
    dispatch(
      putAudioStopped.request({
        id: audioEventIdRef.current,
        duration: playerCurrentTimeRef.current,
      }),
    );
    setCurrentSubtitle('');
    // Check against account.length since that's our actual playlist
    if (next && account && ind < account.length - 1) {
      setInd(ind + 1);
      setAnimating(true);
      // Play the intro sound if audio should auto-play based on current play state
      if (shouldPlayRef.current) {
        const promisePlay = audio.play();
        if (promisePlay !== undefined) {
          promisePlay.then().catch((error) => {
            showInfo({ text: error, title: t('Please allow sound on this page for better experience') });
          });
        }
      }
    } else if (!next && ind > 0) {
      setInd(ind - 1);
    }
  };

  const handleClose = () => {
    dispatch(handleOpenPlayer.request({ list: [], account: null, index: 0 }));
  };
  const handleEnded = () => {
    isEndingRef.current = true; // tell onPause to ignore this pause
    setShouldPlay(true);
    shouldPlayRef.current = true;
    onControlClick(true, true)(); // existing behavior (jingle + animation)
  };

  // onPause: ignore pauses caused by natural end or during animation/jingle
  const handlePause: React.ComponentProps<typeof AudioPlayer>['onPause'] = (e?: any) => {
    const ended = !!e?.target?.ended;
    if (ended || isEndingRef.current || animating) {
      // do NOT change shouldPlay when the media naturally ended or while jingle/animation runs
      return;
    }
    setIsPaused(true);
    isPausedRef.current = true;
    setShouldPlay(false);
    shouldPlayRef.current = false;

    // Pause video if current
    if (isCurrentVideo) {
      const mediaMap = (window as any).__storyMedia || {};
      const videoRef: HTMLVideoElement | undefined = mediaMap?.[activeIndex]?.video;
      videoRef?.pause();
    }
  };

  // onPlay: clear the "ending" marker once new media actually starts
  const handlePlay = () => {
    isEndingRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    setShouldPlay(true);
    shouldPlayRef.current = true;
    if (isInitialOpen) setIsInitialOpen(false);

    // Send audio-played request only once per track when actually played
    if (!audioPlayedSentRef.current && eventIdRef.current) {
      dispatch(postAudioPlayed.request({ event_detail_id: eventIdRef.current }));
      audioPlayedSentRef.current = true;
    }

    // Play video if current
    if (isCurrentVideo) {
      const mediaMap = (window as any).__storyMedia || {};
      const videoRef: HTMLVideoElement | undefined = mediaMap?.[activeIndex]?.video;
      videoRef?.play().catch();
    }
  };

  // Expose global mute setter and pause state for video overlay
  useEffect(() => {
    (window as any).__setGlobalMute = (muted: boolean) => {
      setIsMuted(muted);
      isMutedRef.current = muted;
      // The AudioPlayer will respect the muted prop we pass to it
      // No need to manually set audioEl.muted here as the muted prop handles it
      // Sync with videos (on desktop only - mobile videos always muted)
      if (!isMobile) {
        const mediaMap = (window as any).__storyMedia || {};
        const videoRef: HTMLVideoElement | undefined = mediaMap?.[activeIndex]?.video;
        if (videoRef) videoRef.muted = muted;
      }
      sessionStorage.setItem('globalMuted', String(muted));
    };
    // Expose pause state
    (window as any).__playerIsPaused = isPausedRef.current;
    return () => {
      delete (window as any).__setGlobalMute;
      delete (window as any).__playerIsPaused;
    };
  }, [activeIndex]);

  // Update global pause state when it changes
  useEffect(() => {
    (window as any).__playerIsPaused = isPausedRef.current;
  }, [isPaused]);

  return (
    <AudioWrapper style={{ bottom: '0' }}>
      <ContainerWrapper ref={ref} as={FLexWrapper}>
        {animating && (
          <Lottie
            speed={20}
            options={defaultOptions}
            height={animationHeight}
            width="100%"
            eventListeners={[
              {
                eventName: 'loopComplete',
                callback: () => {
                  setAnimating(false);
                  // After animation completes, ensure auto-play state is applied
                  if (shouldPlayRef.current && player.current?.audio?.current) {
                    setTimeout(() => {
                      player.current?.audio?.current?.play()?.catch((err: any) => {
                        console.log('Auto-play after animation failed:', err);
                      });
                      // jingle phase is over, allow normal pause handling again
                      isEndingRef.current = false;
                    }, 100);
                  } else {
                    // if user had paused, stay paused after navigation
                    isEndingRef.current = false;
                  }
                },
              },
            ]}
          />
        )}
        {!animating && (
          <>
            <CardWrapper>
              <ImgWrapper
                loading="lazy"
                src={avatarImg}
                onError={() => setAvatarImg(ErrorImg)}
                alt="title"
                draggable={false}
                onClick={() =>
                  currentItem?.id && history.push(`/event/${currentItem?.eventId}/${lng}/${currentItem?.title}`)
                }
              />
              <DescriptionCard>
                <TitleArea
                  onClick={() =>
                    currentItem?.id && history.push(`/event/${currentItem?.eventId}/${lng}/${currentItem?.title}`)
                  }
                >
                  <u>{t('Events details')}</u>
                  {isMobile && currentItem?.booking_url && (
                    <MobileBookingRow>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          window.open(currentItem?.booking_url as string, '_blank');
                        }}
                        style={{
                          background: theme?.primary?.[0] || '#EE3932',
                          borderColor: theme?.primary?.[0] || '#EE3932',
                          borderRadius: '10px',
                          fontSize: '12px',
                          height: '24px',
                          padding: '0 12px',
                        }}
                      >
                        {t('Book')}
                      </Button>
                    </MobileBookingRow>
                  )}
                </TitleArea>

                {currentItem?.title && !isMobile && (
                  <PriceArea
                    className="price"
                    onClick={() =>
                      (currentItem?.plan?.type == ALL_INCLUSIVE || currentItem?.plan?.type == MULTI_CHANNEL) &&
                      !iframeParams.isIframe &&
                      history.push(`/@${currentItem?.alias}`)
                    }
                  >
                    {currentItem?.screen_name}
                  </PriceArea>
                )}
              </DescriptionCard>
              {!isMobile && (
                <Button
                  className={'mobile-only'}
                  type="text"
                  ghost
                  style={{ padding: '0', width: '2em', height: '2em' }}
                  onClick={handleClose}
                >
                  <FiX size="2em" color={playerColor} />
                </Button>
              )}
            </CardWrapper>

            <AudioPlayer
              autoPlay={shouldPlay}
              autoPlayAfterSrcChange={shouldPlay}
              muted={isMuted}
              onVolumeChange={(e: any) => {
                // Check if the audio element's muted state has changed
                const audioEl = e.target as HTMLAudioElement;
                if (audioEl && audioEl.muted !== isMutedRef.current) {
                  // User clicked the mute button in the player
                  const newMuted = audioEl.muted;
                  setIsMuted(newMuted);
                  isMutedRef.current = newMuted;
                  sessionStorage.setItem('globalMuted', String(newMuted));

                  // Sync with videos (on desktop only - mobile videos always muted)
                  if (!isMobile) {
                    const mediaMap = (window as any).__storyMedia || {};
                    const videoRef: HTMLVideoElement | undefined = mediaMap?.[activeIndex]?.video;
                    if (videoRef) videoRef.muted = newMuted;
                  }

                  // Notify other components
                  if ((window as any).__setGlobalMute) {
                    (window as any).__setGlobalMute(newMuted);
                  }
                }
              }}
              showSkipControls={false}
              showJumpControls={false}
              layout="stacked-reverse"
              listenInterval={100}
              onListen={(e: any) => {
                const t = e.target?.currentTime || 0;
                setPlayerCurrentTime(t);
                // Sync video time if current is video
                if (isCurrentVideo) {
                  const mediaMap = (window as any).__storyMedia || {};
                  const videoRef: HTMLVideoElement | undefined = mediaMap?.[activeIndex]?.video;
                  if (videoRef && Math.abs((videoRef.currentTime || 0) - t) > 0.25) {
                    try {
                      videoRef.currentTime = t;
                    } catch {}
                  }
                }
              }}
              customProgressBarSection={[
                <div className="transcription-wrapper">
                  {!isCurrentVideo && (
                    <>
                      <TranscriptionPlayer>
                        <span>{currentSubtitle}</span>
                      </TranscriptionPlayer>
                      <div className="transcription-toggle">
                        {isHidden ? (
                          <MdOutlineSubtitles
                            size="2em"
                            color={playerColor}
                            onClick={() => {
                              setIsHidden(!isHidden);
                            }}
                          />
                        ) : (
                          <MdOutlineSubtitlesOff
                            size="2em"
                            color={playerColor}
                            onClick={() => {
                              setIsHidden(!isHidden);
                            }}
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>,
                RHAP_UI.CURRENT_TIME,
                RHAP_UI.PROGRESS_BAR,
                RHAP_UI.DURATION,
              ]}
              customControlsSection={[
                RHAP_UI.MAIN_CONTROLS,
                <Button
                  className={'mobile-hidden'}
                  type="text"
                  ghost
                  style={{ padding: '0', width: '2em', height: '2em', position: 'absolute', right: 0 }}
                  onClick={handleClose}
                >
                  <FiX size="2em" color={playerColor} />
                </Button>,
              ]}
              customIcons={{
                play: <PlayIcon fill={playerColor} />,
                pause: <PauseIcon fill={playerColor} />,
                previous: <BackIcon fill={playerColor} />,
                next: <ForwardIcon fill={playerColor} />,
              }}
              src={list && list.length ? (list[activeIndex] as any) : undefined}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
              onClickPrevious={onControlClick(false, false)}
              onClickNext={onControlClick(true, false)}
              ref={player}
            />
          </>
        )}
      </ContainerWrapper>
    </AudioWrapper>
  );
};
